/**
 * The public CMS seam for the signed-out pages — FAQ, News, an article, a legal document.
 *
 * Two decisions shaped this file. **First: the server's shapes, not ours.** The FAQ endpoint
 * returns a bare array ordered by `sortOrder`, news returns a `{ items, page, pageSize, total }`
 * envelope, and both answer an empty list rather than a 404 in preview mode
 * (`cms-public.controller.ts`). `fetchNews` normalises either an envelope or a bare array,
 * because the one thing a page must never do is guess which it got and render nothing.
 *
 * **Second: a CMS body is admin input.** It is authored HTML that arrives over the wire and
 * lands in `innerHTML`, so it goes through `sanitizeCmsHtml` first — the same reasoning
 * `legal-api.ts` applies to legal documents and `MetaService` applies to meta values. There is
 * no path in these pages that writes a CMS string into markup unsanitised.
 *
 * A note on the FAQ: `faq_entries` has no category column (`schema.prisma`, confirmed against the
 * live dev API 2026-09-10), so there is nothing to group by and no grouping helper here. The page
 * renders one accordion in the server's `sortOrder`, which is the only ordering that exists.
 */
import DOMPurify from 'dompurify';

import { api } from '../../core/network/api';
import { formatDate } from '../i18n';

/** `GET /cms/faq` — one published question. The endpoint answers a plain array, `sortOrder` asc. */
export interface FaqEntry {
  id: string;
  question: string;
  answer: string;
  sortOrder?: number;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `GET /cms/news` row / `GET /cms/news/:slug` — a dispatch. `summary` is the excerpt. */
export interface NewsArticle {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  imageUrl?: string | null;
  tag?: string;
  published?: boolean;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** The paginated envelope `GET /cms/news` answers with. Page size is the server's, always 20. */
export interface NewsPage {
  items: NewsArticle[];
  page: number;
  pageSize: number;
  total: number;
}

/** `GET /cms/legal/:slug` — an admin-managed legal document, body included. */
export interface LegalDocument {
  id: string;
  slug: string;
  title: string;
  content: string;
  updatedAt?: string;
}

export interface FetchOptions {
  signal?: AbortSignal;
}

export const fetchFaqEntries = (opts: FetchOptions = {}): Promise<FaqEntry[]> =>
  api.get<FaqEntry[] | null>('/cms/faq', opts).then((rows) => (Array.isArray(rows) ? rows : []));

/**
 * News, normalised. The controller returns an envelope, but preview mode and any future
 * un-paginated shape would return a list — a page that reads `.items` off an array shows an
 * empty screen and no error, which is the worst of both. So both are accepted here.
 */
export function normalizeNewsPage(raw: NewsPage | NewsArticle[] | null | undefined): NewsPage {
  if (Array.isArray(raw)) {
    return { items: raw, page: 1, pageSize: raw.length || 20, total: raw.length };
  }
  const items = Array.isArray(raw?.items) ? raw!.items : [];
  return {
    items,
    page: typeof raw?.page === 'number' ? raw.page : 1,
    pageSize: typeof raw?.pageSize === 'number' ? raw.pageSize : 20,
    total: typeof raw?.total === 'number' ? raw.total : items.length,
  };
}

export const fetchNews = (page = 1, opts: FetchOptions = {}): Promise<NewsPage> =>
  api
    .get<NewsPage | NewsArticle[]>(page > 1 ? `/cms/news?page=${page}` : '/cms/news', opts)
    .then(normalizeNewsPage);

export const fetchNewsArticle = (slug: string, opts: FetchOptions = {}): Promise<NewsArticle> =>
  api.get<NewsArticle>(`/cms/news/${encodeURIComponent(slug)}`, opts);

export const fetchLegalDocument = (slug: string, opts: FetchOptions = {}): Promise<LegalDocument> =>
  api.get<LegalDocument>(`/cms/legal/${encodeURIComponent(slug)}`, opts);

/** True for the endpoint's own "not published / not there" answer, which is a state, not a fault. */
export function isNotFound(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 404;
}

/** An aborted request is a visitor who navigated away — never an error state to render. */
export function isAborted(error: unknown): boolean {
  const err = error as { name?: string; code?: string } | null;
  return err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';
}

/**
 * Sanitises admin-authored CMS HTML. Never render a CMS body without this: the content is typed
 * by an admin into a rich-text field, stored verbatim, and served to a signed-out visitor — the
 * exact shape of a stored-XSS delivery. DOMPurify's default profile keeps prose and drops
 * scripts, event handlers and `javascript:` URLs.
 */
export function sanitizeCmsHtml(html: string): string {
  return DOMPurify.sanitize(html ?? '');
}

/** The anchor an entry is deep-linked by. Stable across renders because the id is the row's. */
export const faqAnchorId = (entry: FaqEntry): string => `q-${entry.id}`;

/** The timestamp that means "when this was published", falling back to when the row appeared. */
export const articleTimestamp = (article: NewsArticle): string | null =>
  article.publishedAt ?? article.createdAt ?? null;

/**
 * `10 September 2026`. Explicitly `en-GB` rather than the visitor's locale: every other string
 * on these pages is English until the i18n pass lands, and a date in one language beside prose
 * in another reads like a bug. Returns '' for a missing or unparseable value — a dateless
 * dispatch is still a dispatch.
 */
export function formatArticleDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    // The reader's chosen interface language, not a hardcoded en-GB: this page is translated
    // into six and a Polish visitor should not get an English month (2026-09-18, same defect
    // class as the shell's dates).
    return formatDate(date, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/** A machine-readable stamp for `<time datetime>`, which is not the human string above. */
export function isoDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}
